
import { __ } from 'bits/intl'
import { Component, Prop, Vue, Watch } from 'bits/vue'
import isChromatic from 'chromatic/isChromatic'
import OzBaseDialogBox, {
  OzBaseDialogBoxButtonData,
  OzContainedButtonColorScheme,
} from 'library/v4/components/OzBaseDialogBox.vue'
import OzBox, { OzBoxColors } from 'library/v4/components/OzBox.vue'
import OzInput from 'library/v4/components/OzInput.vue'
// Fix this for Chromatic comparison
const generateCode = (): string =>
  isChromatic()
    ? '0539'
    : Math.floor(Math.random() * 10000)
        .toString()
        .padStart(4, '0')

enum ButtonScheme {
  Default = 'Default',
  Danger = 'Danger',
  PromoteCancellation = 'PromoteCancellation',
  SaveDiscardCancel = 'SaveDiscardCancel',
}

@Component({ components: { OzBox, OzBaseDialogBox, OzInput } })
export default class OzConfirmationDialogBox extends Vue {
  OzBoxColors = OzBoxColors

  codeConfirmationText = __('Enter this code to proceed:')
  confirmationCode = generateCode()
  enteredCode = ''
  isSubmittedCodeValid = true

  $refs!: {
    confirmationCodeInput: OzInput
  }

  /**
   * Dark mode.
   */
  @Prop({ default: 'auto' }) darkMode!: true | false | 'auto'

  /**
   * Icon.
   */
  @Prop({ default: null }) iconSrc!: string | null

  /**
   * Icon alt text.
   */
  @Prop({ default: null }) iconAlt!: string | null

  /**
   * Title.
   */
  @Prop({ required: true }) title!: string

  /**
   * Body.
   */
  @Prop() body?: string

  /**
   * Custom body classes
   */
  @Prop() customBodyClasses?: string

  /**
   * Shadow scheme.
   */
  @Prop({ default: false }) xShadow!: boolean

  /**
   * Confirm with a confirmation code.
   */
  @Prop({ default: false }) isCodeProtected!: boolean

  /**
   * Confirm button text.
   */
  @Prop() confirmButtonText?: string

  /**
   * Cancel button text.
   */
  @Prop() cancelButtonText?: string

  /**
   * Discard button text.
   */
  @Prop() discardButtonText?: string

  /**
   * Pick a button scheme based on the intended use case:
   *
   * 1. `Default` - `confirmButton` is highlighted as the primary action.
   * 1. `Danger` - `confirmButton` is highlighted in red to signal danger.
   * 1. `PromoteCancellation` - `cancelButton` is positioned as the primary action. `confirmButton`'s color is muted.
   */
  @Prop({ default: ButtonScheme.Default }) buttonScheme?: ButtonScheme

  /**
   * Determines if each button should fill the width of the dialog
   */
  @Prop({ default: false }) forceFullWidthButtons!: boolean

  @Watch('enteredCode')
  handleEnteredCode(): void {
    if (this.enteredCode.length > 0) {
      this.isSubmittedCodeValid = true
    }
  }

  get buttons(): OzBaseDialogBoxButtonData[] {
    /**
     * Emits events passed in via the `buttons` prop.
     * @event cancel
     */
    const cancelButton = {
      text: this.cancelButtonText || __('Cancel'),
      emit: 'cancel',
      colorScheme: OzContainedButtonColorScheme.Secondary,
      testId: 'cancelButton',
    }
    /**
     * Emits events passed in via the `buttons` prop.
     * @event confirm
     */
    const confirmButton = {
      text: this.confirmButtonText || __('Continue'),
      emit: 'confirm',
      colorScheme: OzContainedButtonColorScheme.Primary,
      disabled: this.isCodeProtected && !this.enteredCode.trim(),
      testId: 'confirmButton',
      isFocusedOnLoad: true,
    }

    /**
     * Emits events passed in via the `buttons` prop.
     * @event discard
     */
    const discardButton = {
      text: this.discardButtonText || __('Discard changes'),
      emit: 'discard',
      colorScheme: OzContainedButtonColorScheme.SecondaryDestructive,
      testId: 'discardButton',
    }

    // When the buttons are in a row, the prioritized action is right-most.
    // When the buttons are stacked, the prioritized action is top-most.
    let buttonList: OzBaseDialogBoxButtonData[]

    if (this.buttonScheme === ButtonScheme.Default) {
      buttonList = [cancelButton, confirmButton]
    } else if (this.buttonScheme === ButtonScheme.Danger) {
      buttonList = [cancelButton, { ...confirmButton, colorScheme: OzContainedButtonColorScheme.Destructive }]
    } else if (this.buttonScheme === ButtonScheme.PromoteCancellation) {
      buttonList = [{ ...confirmButton, colorScheme: OzContainedButtonColorScheme.SecondaryClear }, cancelButton]
    } else if (this.buttonScheme === ButtonScheme.SaveDiscardCancel) {
      buttonList = [
        { ...cancelButton, colorScheme: OzContainedButtonColorScheme.SecondaryClear },
        discardButton,
        confirmButton,
      ]
    } else {
      buttonList = [cancelButton, confirmButton]
    }

    return this.forceFullWidthButtons ? buttonList.reverse() : buttonList
  }

  focus(): void {
    this.$nextTick(() => this.$refs?.confirmationCodeInput?.focusInput())
  }

  cancel(): void {
    /**
     * Cancel.
     */
    this.$emit('cancel')
  }

  confirm(): void {
    /**
     * Action confirmed.
     */
    this.$emit('confirm')
  }

  discard(): void {
    this.$emit('discard')
  }

  submit(): void {
    if (!this.isCodeProtected) {
      this.confirm()
    } else if (this.enteredCode.trim() === this.confirmationCode) {
      this.confirm()
    } else {
      this.isSubmittedCodeValid = false
      this.focus()
      this.confirmationCode = generateCode()
    }
    this.enteredCode = ''
  }
}

export { ButtonScheme as OzConfirmationDialogBoxButtonScheme }
